/**
 * Stage G — Civil & Structural panel.
 *   G.1  Structural Analysis      → G1Analysis
 *   G.2  Foundation Design         → G2Foundations
 *   G.3  Structural Steel          → G3Steel
 *   G.4  RC Detailed Design (BBS)  → G4Rebar
 *   G.5  Civil MTO                 → G5MTO
 */
import React, { useCallback, useEffect, useState } from "react";
import api from "../lib/api";

const _fmt = (n, d = 1) => (n == null ? "-"
    : typeof n === "number" ? n.toFixed(d) : n);

function StatTile({ label, value, testid }) {
  return (
    <div className="rounded-md border border-neutral-800 bg-neutral-900/60 p-3"
          data-testid={testid}>
      <div className="text-2xl font-bold text-neutral-100">{value}</div>
      <div className="mt-1 text-[10px] uppercase tracking-wider text-neutral-500">{label}</div>
    </div>
  );
}

function Section({ title, children, testid }) {
  return (
    <div className="rounded-md border border-neutral-800 bg-neutral-950 p-4"
          data-testid={testid}>
      <div className="mb-2 text-sm font-semibold text-neutral-200">{title}</div>
      {children}
    </div>
  );
}

function Table({ cols, rows, testid }) {
  return (
    <div className="overflow-x-auto rounded border border-neutral-800"
          data-testid={testid}>
      <table className="w-full text-xs">
        <thead className="bg-neutral-900 text-neutral-400">
          <tr>{cols.map(([, l]) => <th key={l} className="px-2 py-2 text-left font-medium">{l}</th>)}</tr>
        </thead>
        <tbody>
          {(rows || []).map((r, i) => (
            <tr key={i} className="border-t border-neutral-800 text-neutral-200">
              {cols.map(([k]) => <td key={k} className="px-2 py-1.5">{_fmt(r?.[k])}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

async function _openSvg(projectId, path, payload) {
  const r = await api.post(`/projects/${projectId}${path}`, payload || {});
  const blob = new Blob([r.data], { type: "image/svg+xml" });
  window.open(URL.createObjectURL(blob), "_blank");
}

async function _downloadDxf(projectId, path, payload, filename) {
  const r = await api.post(`/projects/${projectId}${path}`, payload || {});
  const blob = new Blob([r.data], { type: "application/dxf" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  a.click(); URL.revokeObjectURL(url);
}

/* ═══════════════════ G.1 · Structural Analysis ══════════════════ */
function G1Analysis({ projectId, res }) {
  const wind = res?.wind_load || {};
  const seis = res?.seismic_load || {};
  const combos = res?.load_combinations || [];
  const ped = res?.octagonal_pedestal_sample;
  const dyn = res?.dynamic_foundation_sample;
  const pile = res?.pile_foundation_option;
  return (
    <div className="space-y-4" data-testid="g1-analysis">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatTile label="Equipment weights"    value={res?.equipment_weights_count ?? 0} testid="g1-tile-weights" />
        <StatTile label="Design wind (kN)"     value={_fmt(wind.force_kN, 1)} testid="g1-tile-wind" />
        <StatTile label="Base shear (kN)"      value={_fmt(seis.base_shear_kN, 1)} testid="g1-tile-seismic" />
        <StatTile label="Load combos"           value={combos.length} testid="g1-tile-combos" />
      </div>

      <Section title="Equipment weights (from Stage C — first 5)" testid="g1-weights-table">
        <Table cols={[
          ["tag", "Tag"], ["type", "Type"],
          ["empty_kN", "Empty (kN)"], ["operating_kN", "Op (kN)"],
          ["test_kN", "Test (kN)"], ["source", "Source"],
        ]} rows={res?.equipment_weights_first5 || []} />
      </Section>

      <Section title="Wind & Seismic loads" testid="g1-loads">
        <div className="grid grid-cols-2 gap-3 text-xs">
          <div className="rounded border border-neutral-800 p-3">
            <div className="font-semibold text-neutral-200 mb-1">Wind</div>
            <div>Design velocity: <b>{_fmt(wind.design_velocity_ms)} m/s</b></div>
            <div>Pressure: <b>{_fmt(wind.height_adjusted_pressure_Pa, 0)} Pa</b></div>
            <div>Force: <b>{_fmt(wind.force_kN)} kN</b></div>
            <div className="text-neutral-500 mt-1">Ref: {wind.code_reference}</div>
          </div>
          <div className="rounded border border-neutral-800 p-3">
            <div className="font-semibold text-neutral-200 mb-1">Seismic</div>
            <div>Ah coefficient: <b>{_fmt(seis.base_shear_coefficient_Ah, 4)}</b></div>
            <div>Base shear: <b>{_fmt(seis.base_shear_kN)} kN</b></div>
            <div>Weight: <b>{_fmt(seis.weight_kN, 0)} kN</b></div>
            <div className="text-neutral-500 mt-1">Ref: {seis.code_reference}</div>
          </div>
        </div>
      </Section>

      <Section title="Load combinations" testid="g1-combos-table">
        <Table cols={[
          ["id", "ID"], ["desc", "Combination"], ["value_kN", "Factored (kN)"],
        ]} rows={combos} />
      </Section>

      {ped && (
        <Section title="Octagonal pedestal (sample tall column)" testid="g1-pedestal">
          <div className="grid grid-cols-2 gap-3 text-xs sm:grid-cols-4">
            <StatTile label="R (m)"        value={_fmt(ped.circumscribed_radius_m, 2)} />
            <StatTile label="Area (m²)"   value={_fmt(ped.area_m2, 2)} />
            <StatTile label="Bearing max" value={_fmt(ped.bearing_pressure_kPa?.max, 1) + " kPa"} />
            <StatTile label="OT wind"      value={ped.overturning?.wind?.status} />
          </div>
        </Section>
      )}

      {dyn && (
        <Section title="Dynamic foundation (compressor sample)" testid="g1-dynamic">
          <div className="text-xs text-neutral-300 space-y-1">
            <div>Machine freq: <b>{dyn.machine_frequency_Hz} Hz</b> · natural freq: <b>{dyn.natural_frequency_Hz} Hz</b></div>
            <div>Frequency ratio: <b>{dyn.frequency_ratio}</b> — resonance <b className={dyn.resonance_check === "PASS" ? "text-emerald-400" : "text-red-400"}>{dyn.resonance_check}</b></div>
            <div>Amplitude: <b>{dyn.amplitude_um} µm</b> vs allowable <b>{dyn.amplitude_allowable_um} µm</b> — <b className={dyn.amplitude_check === "PASS" ? "text-emerald-400" : "text-red-400"}>{dyn.amplitude_check}</b></div>
            <div className="text-neutral-500">Ref: {dyn.reference}</div>
          </div>
        </Section>
      )}

      {pile && (
        <Section title="Pile foundation option" testid="g1-pile">
          <div className="text-xs text-neutral-300 space-y-1">
            <div>{pile.pile_type} · ⌀ {pile.diameter_mm} mm · L {pile.length_m} m</div>
            <div>Safe capacity/pile: <b>{pile.single_pile_safe_capacity_kN} kN</b></div>
            <div>Piles required: <b>{pile.n_piles_required}</b> · cap side {pile.pile_cap_side_m} m</div>
            <div className="text-neutral-500">Ref: {pile.code_reference}</div>
          </div>
        </Section>
      )}
    </div>
  );
}

/* ═══════════════════ G.2 · Foundations ═══════════════════════════ */
function G2Foundations({ projectId, res }) {
  const fdns = res?.foundation_designs_first5 || [];
  const anchor = res?.anchor_bolt_sample || {};
  const loading = res?.foundation_loading_table_first10 || [];
  const bp = res?.baseplate_grout_sample || {};
  return (
    <div className="space-y-4" data-testid="g2-foundations">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatTile label="Foundations"      value={res?.foundation_count ?? 0} testid="g2-tile-count" />
        <StatTile label="Anchor size"       value={anchor.diameter_mm ? "M" + anchor.diameter_mm : "-"} testid="g2-tile-anchor" />
        <StatTile label="Baseplate (mm)"    value={bp.plate_side_mm ?? "-"} testid="g2-tile-plate" />
        <StatTile label="Grout (mm)"         value={bp.grout_thickness_mm ?? "-"} testid="g2-tile-grout" />
      </div>

      <Section title="Foundation designs (first 5)" testid="g2-fdn-table">
        <Table cols={[
          ["tag", "Tag"], ["type_selected", "Type"],
          ["length_m", "L (m)"], ["width_m", "B (m)"], ["depth_m", "D (m)"],
          ["governing_load_kN", "Load (kN)"], ["bearing_check", "Bearing"],
        ]} rows={fdns} />
      </Section>

      <Section title="Foundation loading summary (first 10)" testid="g2-loading-table">
        <Table cols={[
          ["tag", "Tag"], ["type", "Type"],
          ["operating_kN", "Op (kN)"], ["test_kN", "Test (kN)"],
          ["wind_shear_kN", "Wind (kN)"], ["seismic_shear_kN", "Seis (kN)"],
          ["governing_load_kN", "Gov"],
        ]} rows={loading} />
      </Section>

      <Section title="Sample anchor-bolt design" testid="g2-anchor">
        <div className="text-xs text-neutral-300 space-y-1">
          <div>{anchor.n_bolts} × M{anchor.diameter_mm} · {anchor.material} · embedment {anchor.embedment_length_mm} mm</div>
          <div>Tension capacity: <b>{_fmt(anchor.tensile_capacity_kN, 1)} kN</b> · concrete breakout: <b>{_fmt(anchor.concrete_breakout_capacity_kN, 1)} kN</b></div>
          <div>Base plate: {anchor.base_plate_side_mm}×{anchor.base_plate_side_mm}×{anchor.base_plate_thickness_mm} mm</div>
          <div className="text-neutral-500">Ref: {anchor.code_reference}</div>
        </div>
      </Section>

      <div className="flex gap-2 flex-wrap">
        {fdns[0] && <>
          <button className="btn btn-secondary text-xs" data-testid="g2-fdn-svg-btn"
                    onClick={() => _openSvg(projectId,
                      "/stage-g/foundation-ga/svg",
                      { foundation: fdns[0], country: res?.country || "IN" })}>
            Preview foundation GA SVG
          </button>
          <button className="btn btn-secondary text-xs" data-testid="g2-fdn-dxf-btn"
                    onClick={() => _downloadDxf(projectId,
                      "/stage-g/foundation-ga/dxf",
                      { foundation: fdns[0], country: res?.country || "IN" },
                      `fdn-${fdns[0].tag}.dxf`)}>
            Download foundation DXF
          </button>
        </>}
      </div>
    </div>
  );
}

/* ═══════════════════ G.3 · Structural Steel ═════════════════════ */
function G3Steel({ projectId, res }) {
  const rack = res?.pipe_rack_design || {};
  const takeoff = res?.steel_takeoff_summary || {};
  const platform = res?.platform_sample || {};
  const conn = res?.connection_sample || {};
  return (
    <div className="space-y-4" data-testid="g3-steel">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatTile label="Steel (tonnes)"     value={_fmt(takeoff.total_weight_tonnes, 3)} testid="g3-tile-steel" />
        <StatTile label="Column section"    value={rack.columns?.section || "-"} testid="g3-tile-col" />
        <StatTile label="Beam section"       value={rack.beams?.section || "-"} testid="g3-tile-beam" />
        <StatTile label="Sections used"      value={takeoff.sections ?? 0} testid="g3-tile-sections" />
      </div>

      <Section title="Pipe rack geometry" testid="g3-rack-geo">
        <div className="text-xs text-neutral-300 space-y-1">
          <div>{rack.geometry?.n_bays} bays × {rack.geometry?.span_m} m · width {rack.geometry?.width_m} m · {rack.geometry?.tier_count} tiers · height {rack.geometry?.total_height_m} m</div>
          <div>Columns: {rack.columns?.section} · {rack.columns?.count} nos · {rack.columns?.total_length_m} m</div>
          <div>Beams: {rack.beams?.section} · {rack.beams?.total_length_m} m · UDL {rack.beams?.udl_kN_m} kN/m</div>
          <div>Bracing: {rack.bracing?.section} · {rack.bracing?.total_length_m} m ({rack.bracing?.type})</div>
          <div>Base plate: {rack.base_plate?.size_mm} mm · {rack.base_plate?.anchor_bolts}</div>
          <div className="text-neutral-500">Ref: {rack.code_reference}</div>
        </div>
      </Section>

      <Section title="Sample platform + access" testid="g3-platform">
        <div className="text-xs text-neutral-300 space-y-1">
          <div>Elevation: {platform.elevation_m} m · {platform.platform_size_m?.join(" × ")} m · load {platform.live_load_kPa} kPa</div>
          <div>Grating: {platform.grating?.specification}</div>
          <div>Handrail: {platform.handrail?.height_mm} mm · access: {platform.access?.type}</div>
        </div>
      </Section>

      <Section title="Sample connection design" testid="g3-connection">
        <div className="text-xs text-neutral-300 space-y-1">
          <div>{conn.connection_type} on {conn.member}</div>
          <div>Bolts: {conn.bolts?.quantity} × {conn.bolts?.diameter} {conn.bolts?.grade} · capacity {conn.bolts?.total_capacity_kN} kN</div>
          <div>Welds: {conn.welds?.size_mm} mm fillet × {conn.welds?.length_mm} mm ({conn.welds?.electrode})</div>
          <div className="text-neutral-500">Ref: {conn.code_reference}</div>
        </div>
      </Section>

      <div className="flex gap-2 flex-wrap">
        <button className="btn btn-secondary text-xs" data-testid="g3-steel-svg-btn"
                  onClick={() => _openSvg(projectId,
                    "/stage-g/steel-ga/svg",
                    { n_bays: rack.geometry?.n_bays || 6,
                       span_m: rack.geometry?.span_m || 6,
                       width_m: rack.geometry?.width_m || 8,
                       tier_count: rack.geometry?.tier_count || 2,
                       country: res?.country || "IN" })}>
          Preview steel GA SVG
        </button>
      </div>
    </div>
  );
}

/* ═══════════════════ G.4 · RC / BBS ══════════════════════════════ */
function G4Rebar({ projectId, res }) {
  const bbs = res?.bbs_summary || {};
  const mix = res?.mix_design || {};
  const foundations = res?.foundation_designs_first5 || [];
  const byDia = bbs.by_dia || {};
  const diaRows = Object.entries(byDia).map(([d, agg]) => ({
    dia_mm: d, length_m: agg.total_length_m,
    weight_kg: agg.total_weight_kg, quantity: agg.quantity,
  }));
  return (
    <div className="space-y-4" data-testid="g4-rebar">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatTile label="Bars in BBS"       value={bbs.count ?? 0} testid="g4-tile-bars" />
        <StatTile label="Rebar (tonnes)"     value={_fmt(bbs.tonnes, 3)} testid="g4-tile-tonnes" />
        <StatTile label="Concrete grade"    value={mix.exposure ? "M25/C25" : "-"} testid="g4-tile-grade" />
        <StatTile label="w/c ratio"          value={mix.w_c_ratio ?? "-"} testid="g4-tile-wc" />
      </div>

      <Section title="BBS summary by diameter" testid="g4-bbs-table">
        <Table cols={[
          ["dia_mm", "⌀ (mm)"], ["length_m", "Length (m)"],
          ["weight_kg", "Weight (kg)"], ["quantity", "Bar count"],
        ]} rows={diaRows} />
      </Section>

      <Section title="Concrete mix design" testid="g4-mix">
        <div className="text-xs text-neutral-300 space-y-1">
          <div>Characteristic strength: <b>{mix.fck} MPa</b></div>
          <div>w/c ratio: <b>{mix.w_c_ratio}</b></div>
          <div>Cement: <b>{mix.cement_kg_m3} kg/m³</b></div>
          <div>Water: <b>{mix.water_kg_m3} kg/m³</b></div>
          <div>Fine aggregate: <b>{mix.fine_agg_kg_m3} kg/m³</b></div>
          <div>Coarse 20 mm: <b>{mix.coarse_20mm_kg_m3} kg/m³</b> · 10 mm: <b>{mix.coarse_10mm_kg_m3} kg/m³</b></div>
          <div>Admixture: <b>{mix.admixture}</b> · slump <b>{mix.slump_mm} mm</b></div>
          <div className="text-neutral-500">Exposure: {mix.exposure}</div>
        </div>
      </Section>

      <div className="flex gap-2 flex-wrap">
        <button className="btn btn-secondary text-xs" data-testid="g4-bbs-svg-btn"
                  onClick={() => _openSvg(projectId,
                    "/stage-g/bbs/svg",
                    { foundations })}>
          Preview BBS drawing
        </button>
      </div>
    </div>
  );
}

/* ═══════════════════ G.5 · Civil MTO ═════════════════════════════ */
function G5MTO({ projectId, res }) {
  const mto = res?.civil_mto || {};
  const proc = res?.civil_procurement || [];
  const seq = res?.construction_sequence_first5 || [];
  const comp = res?.completeness || {};
  const totals = mto.totals || {};
  return (
    <div className="space-y-4" data-testid="g5-mto">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatTile label="Concrete (m³)"    value={_fmt(totals.concrete_m3, 1)} testid="g5-tile-conc" />
        <StatTile label="Rebar (tonnes)"    value={_fmt(totals.rebar_tonnes, 2)} testid="g5-tile-rebar" />
        <StatTile label="Steel (tonnes)"    value={_fmt(totals.structural_steel_tonnes, 2)} testid="g5-tile-steel" />
        <StatTile label="Formwork (m²)"    value={_fmt(totals.formwork_m2, 0)} testid="g5-tile-formwork" />
        <StatTile label="Earthwork (m³)"    value={_fmt(totals.earthwork_m3, 0)} testid="g5-tile-earth" />
        <StatTile label="Backfill (m³)"     value={_fmt(totals.backfill_m3, 0)} testid="g5-tile-backfill" />
        <StatTile label="PCC (m³)"           value={_fmt(totals.pcc_m3, 1)} testid="g5-tile-pcc" />
        <StatTile label="Foundations"        value={totals.foundations_count ?? 0} testid="g5-tile-fdn" />
      </div>

      <Section title={`Civil MTO — ${mto.format}`} testid="g5-mto-table">
        <Table cols={[
          ["code", "Code"], ["item", "Item"],
          ["qty", "Qty"], ["unit", "Unit"],
        ]} rows={mto.line_items || []} />
      </Section>

      <Section title="Civil procurement schedule" testid="g5-procure-table">
        <Table cols={[
          ["package", "Package"], ["item", "Item"],
          ["qty", "Qty"], ["unit", "Unit"],
          ["long_lead", "Long lead"], ["lead_time_weeks", "Lead (w)"],
        ]} rows={proc} />
      </Section>

      <Section title={`Construction sequence (first 5 of ${res?.construction_sequence_count})`} testid="g5-seq-table">
        <Table cols={[
          ["step", "#"], ["activity", "Activity"],
          ["duration_days", "Days"], ["deliverable", "Deliverable"],
        ]} rows={seq} />
      </Section>

      <Section title="Completeness (G.5-F5)" testid="g5-complete">
        <div className="text-xs text-neutral-300 space-y-1">
          <div>Status: <b>{comp.status}</b> ({comp.percent_complete}%)</div>
          <div>Pass: {comp.count_pass} / {comp.count_total}</div>
          {(comp.missing || []).length > 0 && (
            <div className="text-amber-300">Missing: {(comp.missing || []).slice(0, 5).join(", ")}</div>
          )}
        </div>
      </Section>

      <div className="flex gap-2 flex-wrap">
        <button className="btn btn-secondary text-xs" data-testid="g5-layout-svg-btn"
                  onClick={() => _openSvg(projectId,
                    "/stage-g/foundation-layout/svg",
                    { plot_size_m: [100, 60],
                       foundations: res?.foundation_designs_first5 || [],
                       country: res?.country || "IN" })}>
          Preview foundation layout SVG
        </button>
      </div>
    </div>
  );
}

/* ═════════════════════ Wrapper ════════════════════════════════ */
export default function StageGPanel({ projectId, subStage }) {
  const [res, setRes] = useState(null);
  const [loading, setLoading] = useState(true);

  const run = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.post(`/projects/${projectId}/stage-g/run`,
                                       { country: "IN", wind_speed_ms: 44,
                                         seismic_zone: "III", sbc_kPa: 150,
                                         plot_size_m: [100, 60] });
      setRes(r.data);
    } catch (e) {
      setRes({ error: e.message });
    } finally { setLoading(false); }
  }, [projectId]);

  useEffect(() => { run(); }, [run]);

  return (
    <div className="space-y-4" data-testid={`stage-g-panel-${subStage}`}>
      <div className="flex items-center justify-between rounded-md border border-neutral-800 bg-neutral-950 p-3">
        <div>
          <div className="text-sm font-semibold text-neutral-100">Stage G · Civil &amp; Structural</div>
          <div className="text-xs text-neutral-400">
            Sub-stage {subStage} · powered by G.1-G.5 gap-closure engines (28 fixes)
          </div>
        </div>
        <button onClick={run} className="btn btn-primary text-xs"
                  data-testid="stage-g-rerun-btn">Re-run pipeline</button>
      </div>

      {loading && (
        <div className="rounded-md border border-neutral-800 bg-neutral-950 p-4 text-xs text-neutral-500"
              data-testid="stage-g-loading">
          Running Stage G pipeline · weights from Stage C · wind/seismic · foundations · steel · BBS · MTO…
        </div>
      )}

      {!loading && res?.error && (
        <div className="rounded-md border border-red-900 bg-red-950/50 p-4 text-xs text-red-300"
              data-testid="stage-g-error">
          Pipeline error: {res.error}
        </div>
      )}

      {!loading && !res?.error && (
        <>
          {subStage === "G.1" && <G1Analysis    projectId={projectId} res={res} />}
          {subStage === "G.2" && <G2Foundations projectId={projectId} res={res} />}
          {subStage === "G.3" && <G3Steel       projectId={projectId} res={res} />}
          {subStage === "G.4" && <G4Rebar       projectId={projectId} res={res} />}
          {subStage === "G.5" && <G5MTO         projectId={projectId} res={res} />}
        </>
      )}
    </div>
  );
}
